import { isUKNumber } from './noteLinks';

export const CONTACT_FIELDS = ['collectionContactName', 'collectionContactPhone', 'collectionContactEmail',
  'deliveryContactName', 'deliveryContactPhone', 'deliveryContactEmail'] as const;
export type ContactField = typeof CONTACT_FIELDS[number];
export type ContactForm = Record<ContactField, string>;
export const EMPTY_CONTACTS: ContactForm = {
  collectionContactName: '', collectionContactPhone: '', collectionContactEmail: '',
  deliveryContactName: '', deliveryContactPhone: '', deliveryContactEmail: '',
};

/** Same full-field UK validation as the live job callables. */
export function contactTelHref(value: string): string | null {
  const phone = value.trim();
  if (!phone || !/^[\d +().-]+$/.test(phone)) return null;
  let digits = phone.replace(/\D/g, '');
  if (phone.startsWith('+')) {
    if (!phone.startsWith('+44')) return null;
    digits = digits.slice(2);
    if (/^\+44[ .-]?\(0\)/.test(phone)) digits = digits.slice(1);
    digits = `0${digits}`;
  } else if (!/^\(?0/.test(phone)) return null;
  return isUKNumber(digits) ? `tel:${digits}` : null;
}

export function contactForm(job: Partial<Record<ContactField, string | null>>): ContactForm {
  return Object.fromEntries(CONTACT_FIELDS.map(field => [field, job[field] ?? ''])) as ContactForm;
}

export function contactErrors(form: ContactForm, emailEnabled: boolean): Partial<Record<ContactField, string>> {
  const errors: Partial<Record<ContactField, string>> = {};
  for (const field of CONTACT_FIELDS) {
    if (field.endsWith('Email') && !emailEnabled) continue;
    const value = form[field].trim();
    const label = `${field.startsWith('collection') ? 'Collection' : 'Delivery'} ${field.endsWith('Phone') ? 'phone' : field.endsWith('Email') ? 'email' : 'contact name'}`;
    const limit = field.endsWith('Phone') ? 30 : field.endsWith('Email') ? 254 : 120;
    if (value.length > limit) errors[field] = `${label} must be at most ${limit} characters.`;
    else if (value && field.endsWith('Phone') && !contactTelHref(value)) errors[field] = `${label} must be a UK phone number.`;
    else if (value && field.endsWith('Email') && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) errors[field] = `${label} must be an email address.`;
  }
  return errors;
}

/** Hidden emails are omitted, preserving any existing value on an edit. */
export function contactPayload(form: ContactForm, emailEnabled: boolean, previous?: Partial<Record<ContactField, string | null>>) {
  return Object.fromEntries(CONTACT_FIELDS.filter(field => emailEnabled || !field.endsWith('Email'))
    .filter(field => previous ? form[field].trim() !== (previous[field] ?? '').trim() : !!form[field].trim())
    .map(field => [field, form[field].trim()]));
}
